/**
 * `#uc2Card` — the clicked watershed (client §3.1): the class badge (with the reason when
 * the watershed could not be assessed), then three small charts, each with one short
 * caption — cropland share by year, rainfall by year with the 2017–2025 average line, and
 * practice adoption against the programme mean.
 *
 * A country row is not a verdict (backend §3.1): it carries counts, hectares and no
 * series, so the card prints those instead of three empty charts.
 *
 * `Uc2Charts` is the same three charts without the captions, exported for `#uc2Compare`,
 * which draws them for two watersheds side by side from the two detail payloads.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { IntelUnitDetail } from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { BarSeries, type BarSeriesBar } from './charts/BarSeries';
import { croplandCaption, practiceCaption, rainCaption } from './uc2Captions';
import {
  PRACTICE_BASE,
  UC2_PRACTICE_GROUPS,
  familySteps,
  normalisePractice,
} from './uc2Constants';

/** The practice bars' own colour — the dark end of the practices chip's family. */
const PRACTICE_COLOR = familySteps(PRACTICE_BASE)[2];

/** Which of the three charts a caller wants — the sub-tabs split them across two panels. */
export type Uc2ChartName = 'cropland' | 'rain' | 'practices';

export interface Uc2ChartsProps {
  detail: IntelUnitDetail;
  /** `#uc2Practice` — which group the third chart is about. */
  practice: string;
  /**
   * The charts to draw, in case the caller wants fewer than all three: the Trends tab
   * takes cropland and rainfall, the Practices tab takes adoption. Omitted means all
   * three, which is what `#uc2Compare` still asks for.
   */
  only?: readonly Uc2ChartName[];
  /** Suffix for each chart's `data-testid`, so two sides of a compare stay addressable. */
  testPrefix: string;
  /**
   * What the card slots between the charts: its three captions, and the `#uc2Practice`
   * selector, which the client's §3.1 puts **above** the practices chart. `#uc2Compare`
   * passes none of them and gets the three bare charts.
   */
  slots?: {
    afterCropland?: ReactNode;
    afterRain?: ReactNode;
    beforePractices?: ReactNode;
    afterPractices?: ReactNode;
  };
}

export function Uc2Charts({ detail, practice, testPrefix, slots, only }: Uc2ChartsProps) {
  const { t } = useTranslation();
  const group = normalisePractice(practice);
  const wanted = (name: Uc2ChartName) => !only || only.includes(name);
  const cropland = detail.series?.cropland ?? [];
  const rain = detail.series?.rain ?? [];
  const adoption = detail.adoption ?? {};
  const means = detail.programme_means ?? {};

  const croplandBars: BarSeriesBar[] = cropland.map((p) => ({
    id: String(p.year),
    label: String(p.year),
    value: typeof p.pct === 'number' ? p.pct : null,
  }));
  const rainBars: BarSeriesBar[] = rain.map((p) => ({
    id: String(p.year),
    label: String(p.year),
    value: typeof p.mm === 'number' ? p.mm : null,
  }));
  const practiceBars: BarSeriesBar[] = UC2_PRACTICE_GROUPS.map((g) => ({
    id: g,
    label: t(`intel.uc2.practice.${g}`),
    value: typeof adoption[g] === 'number' ? (adoption[g] as number) : null,
    highlight: g === group,
    color: PRACTICE_COLOR,
  }));
  const groupMean = typeof means[group] === 'number' ? (means[group] as number) : null;

  return (
    <>
      {wanted('cropland') && croplandBars.length ? (
        <div className="uc2-chart" data-testid={`uc2-chart-cropland-${testPrefix}`}>
          <h6 className="intel-block-title">{t('intel.uc2.chart.cropland')}</h6>
          <BarSeries
            bars={croplandBars}
            unit="%"
            ariaLabel={t('intel.uc2.chart.cropland')}
            testId={`uc2-bars-cropland-${testPrefix}`}
          />
        </div>
      ) : null}
      {wanted('cropland') ? slots?.afterCropland : null}
      {wanted('rain') && rainBars.length ? (
        <div className="uc2-chart" data-testid={`uc2-chart-rain-${testPrefix}`}>
          <h6 className="intel-block-title">{t('intel.uc2.chart.rain')}</h6>
          <BarSeries
            bars={rainBars}
            unit="mm"
            decimals={0}
            mean={typeof detail.rain_mean === 'number' ? detail.rain_mean : null}
            meanLabel={t('intel.uc2.chart.rainMean')}
            ariaLabel={t('intel.uc2.chart.rain')}
            testId={`uc2-bars-rain-${testPrefix}`}
          />
        </div>
      ) : null}
      {wanted('rain') ? slots?.afterRain : null}
      {wanted('practices') ? slots?.beforePractices : null}
      {wanted('practices') && practiceBars.some((b) => b.value != null) ? (
        <div className="uc2-chart" data-testid={`uc2-chart-practices-${testPrefix}`}>
          <h6 className="intel-block-title">{t('intel.uc2.chart.practices')}</h6>
          <BarSeries
            bars={practiceBars}
            unit="%"
            mean={groupMean}
            meanLabel={t('intel.uc2.chart.programmeMean')}
            ariaLabel={t('intel.uc2.chart.practices')}
            testId={`uc2-bars-practices-${testPrefix}`}
          />
        </div>
      ) : null}
      {wanted('practices') ? slots?.afterPractices : null}
    </>
  );
}

export interface Uc2CardProps {
  detail: IntelUnitDetail;
  practice: string;
  /** `#uc2Practice`, rendered above the practices chart by the pane that owns the store. */
  practiceSelect: ReactNode;
  /**
   * The run's own `summary.stability_band`, in percent — the three captions read the same
   * band the engine classified with, rather than a constant that drifts from the config.
   */
  band?: number;
  /** Which charts this panel wants; omitted means all three. */
  only?: readonly Uc2ChartName[];
  /**
   * Anything the panel wants inside the card, after the charts — the Trends tab's
   * vegetation chart, which belongs in the same bordered box as the other two rather than
   * floating under it.
   */
  after?: ReactNode;
}

export function Uc2Card({ detail, practice, practiceSelect, band, only, after }: Uc2CardProps) {
  const { t } = useTranslation();
  const group = normalisePractice(practice);
  const isCountry = detail.unit.level === 'admin0';
  const counts = detail.counts ?? {};
  const rain = detail.series?.rain ?? [];
  const last = rain.length ? rain[rain.length - 1]! : null;
  const adoption = detail.adoption ?? {};
  const means = detail.programme_means ?? {};

  const captions = {
    cropland: croplandCaption(detail.cropland_delta_pct, band),
    rain: rainCaption(last ? { year: last.year, mm: last.mm } : null, detail.rain_mean, band),
    practices: practiceCaption(
      t(`intel.uc2.practice.${group}`),
      typeof adoption[group] === 'number' ? (adoption[group] as number) : null,
      typeof means[group] === 'number' ? (means[group] as number) : null,
      band,
    ),
  };

  return (
    <div id="uc2Card" className="intel-selected result-box">
      <h5>
        {detail.unit.name} — <span data-testid="uc2-badge">{detail.category_label}</span>
      </h5>
      <div className="result-pad">
        {detail.category === 'not_assessed' && detail.reason ? (
          <p className="note" data-testid="uc2-not-assessed">
            {t(`intel.uc2.reasonLong.${detail.reason}`, {
              defaultValue: t('intel.uc2.legend.notAssessedReason', {
                reason: t(`intel.uc2.reason.${detail.reason}`, { defaultValue: detail.reason }),
              }),
            })}
          </p>
        ) : null}

        {isCountry ? (
          <ul className="uc2-counts" data-testid="uc2-country-counts">
            <li>{t('intel.uc2.country.positive', { n: counts.positive ?? 0 })}</li>
            <li>{t('intel.uc2.country.noPattern', { n: counts.no_pattern ?? 0 })}</li>
            <li>{t('intel.uc2.country.notAssessed', { n: counts.not_assessed ?? 0 })}</li>
            <li>
              {t('intel.uc2.country.area', {
                value: fmtNum(detail.positive_area_ha ?? 0, 0),
              })}
            </li>
          </ul>
        ) : (
          <Uc2Charts
            detail={detail}
            practice={group}
            testPrefix="card"
            {...(only ? { only } : {})}
            slots={{
              afterCropland: captions.cropland ? (
                <p id="uc2Caption-cropland" className="sub">
                  {t(captions.cropland.key, captions.cropland.options ?? {})}
                </p>
              ) : null,
              afterRain: captions.rain ? (
                <p id="uc2Caption-rain" className="sub">
                  {t(captions.rain.key, captions.rain.options ?? {})}
                </p>
              ) : null,
              beforePractices: practiceSelect,
              afterPractices: captions.practices ? (
                <p id="uc2Caption-practices" className="sub">
                  {t(captions.practices.key, captions.practices.options ?? {})}
                </p>
              ) : null,
            }}
          />
        )}
        {after}
      </div>
    </div>
  );
}

export default Uc2Card;
